/**
 * Hook for managing NWC connection persistence via a DataSource interface.
 *
 * Provides CRUD operations for NWC wallet connections: add, remove, set active,
 * update label. Uses the DataSource pattern for testability (same as Phase 3
 * Cashu hooks). The first connection added is automatically set as active.
 * Removing the active connection reassigns to the next by creation order.
 *
 * Tier: 2 (Lightning features).
 *
 * @module lightning/hooks/useNwcConnections
 */

import { useMemo, useCallback } from 'react';
import { useActiveConnectionOverride } from '@/contexts/ActiveConnectionProvider';

/** A single NWC connection record. */
export interface NwcConnectionRecord {
  /** Unique identifier. */
  readonly id: string;
  /** Connection type (e.g., 'nwc'). */
  readonly type: string;
  /** User-facing label. */
  readonly label: string;
  /** Encrypted NWC connection secret (URI). */
  readonly connectionSecret: string;
  /** Wallet pubkey from the NWC URI. */
  readonly pubkey: string;
  /** Relay URL from the NWC URI. */
  readonly relayUrl: string;
  /** Whether this is the currently active connection. */
  readonly isActive: boolean;
  /** ISO timestamp of last successful connection, or null. */
  readonly lastConnectedAt: string | null;
  /** Last known balance in satoshis (for aggregated display when disconnected). */
  readonly lastKnownBalanceSats: number | null;
  /** NIP-47 capabilities reported by the wallet, or null. */
  readonly capabilities: string[] | null;
}

/** Data for creating a new NWC connection. */
export interface AddConnectionData {
  /** Connection type (e.g., 'nwc'). */
  readonly type: string;
  /** User-facing label. */
  readonly label: string;
  /** Encrypted NWC connection secret (URI). */
  readonly connectionSecret: string;
  /** Wallet pubkey. */
  readonly pubkey: string;
  /** Relay URL. */
  readonly relayUrl: string;
  /** NIP-47 capabilities. */
  readonly capabilities?: string[];
}

/** Data for updating an existing NWC connection. */
export interface UpdateConnectionData {
  /** Updated label. */
  readonly label?: string;
  /** Updated last connected timestamp. */
  readonly lastConnectedAt?: string;
  /** Updated capabilities. */
  readonly capabilities?: string[];
  /** Updated active state. */
  readonly isActive?: boolean;
  /** Updated last known balance in satoshis. */
  readonly lastKnownBalanceSats?: number;
}

/** DataSource interface for NWC connections (decoupled from Evolu). */
export interface NwcConnectionsDataSource {
  /** All NWC connection records. */
  readonly connections: readonly NwcConnectionRecord[];
  /** Add a new connection. */
  readonly addConnection: (data: AddConnectionData) => void;
  /** Soft-delete a connection by ID. */
  readonly removeConnection: (id: string) => void;
  /** Update a connection by ID. */
  readonly updateConnection: (id: string, data: UpdateConnectionData) => void;
}

/** Return type for the useNwcConnections hook. */
export interface UseNwcConnectionsResult {
  /** All connections (not deleted). */
  readonly connections: readonly NwcConnectionRecord[];
  /** The currently active connection, or null. */
  readonly activeConnection: NwcConnectionRecord | null;
  /** Add a new connection. First one auto-activates. Returns duplicate pubkey warning if applicable. */
  readonly addConnection: (data: AddConnectionData) => { duplicatePubkey: boolean };
  /** Remove a connection by ID. If it was active, reassigns to next. */
  readonly removeConnection: (id: string) => void;
  /** Set a connection as the active one (deactivates others). */
  readonly setActive: (id: string) => void;
  /** Update the label of a connection. */
  readonly updateLabel: (id: string, label: string) => void;
}

/**
 * Manage NWC connections via a DataSource interface.
 *
 * Provides CRUD with automatic active-connection management:
 * - First connection added becomes active automatically
 * - Removing the active connection promotes the next by creation order
 * - setActive deactivates all others
 *
 * @param dataSource - DataSource providing connection persistence
 * @returns Object with connections, activeConnection, and CRUD methods
 */
export function useNwcConnections(dataSource: NwcConnectionsDataSource): UseNwcConnectionsResult {
  const { connections, updateConnection } = dataSource;

  // Shared context override for active connection ID.
  // Evolu's db.update for isActive doesn't reliably trigger reactive query
  // updates, so we track the active connection in React context for
  // immediate UI response across all components.
  const override = useActiveConnectionOverride();
  const activeOverrideId = override?.activeOverrideId ?? null;

  const activeConnection = useMemo(() => {
    if (activeOverrideId) {
      return connections.find((c) => c.id === activeOverrideId) ?? null;
    }
    return connections.find((c) => c.isActive) ?? null;
  }, [connections, activeOverrideId]);

  const addConnection = useCallback(
    (data: AddConnectionData): { duplicatePubkey: boolean } => {
      const duplicatePubkey = connections.some((c) => c.pubkey === data.pubkey);
      const isFirst = connections.length === 0;

      dataSource.addConnection({
        ...data,
      });

      // If this is the first connection, the data source should set it active.
      // We handle this by updating immediately after add if it's the first.
      // The DataSource is expected to set isActive based on the connection list.
      // For simplicity, if first, we trigger an update after add.
      if (isFirst) {
        // The connection was just added -- we need its ID to set active.
        // Since we don't get the ID back from addConnection, the DataSource
        // should handle isActive=true for the first connection.
        // This is a limitation of the interface -- the Evolu implementation
        // will handle this by checking connection count before insert.
      }

      return { duplicatePubkey };
    },
    [connections, dataSource],
  );

  const removeConnection = useCallback(
    (id: string): void => {
      const wasActive = connections.find((c) => c.id === id)?.isActive ?? false;
      dataSource.removeConnection(id);

      // If the removed connection was active, reassign to next by creation order
      if (wasActive) {
        const remaining = connections.filter((c) => c.id !== id);
        if (remaining.length > 0 && remaining[0]) {
          updateConnection(remaining[0].id, { isActive: true });
        }
      }
    },
    [connections, dataSource, updateConnection],
  );

  const setActive = useCallback(
    (id: string): void => {
      // Set shared context immediately for instant UI response across all components
      override?.setActiveOverrideId(id);

      // Also update Evolu in the background for persistence across page loads
      for (const conn of connections) {
        if (conn.id === id && !conn.isActive) {
          updateConnection(conn.id, { isActive: true });
        } else if (conn.id !== id && conn.isActive) {
          updateConnection(conn.id, { isActive: false });
        }
      }
    },
    [connections, updateConnection, override],
  );

  const updateLabel = useCallback(
    (id: string, label: string): void => {
      updateConnection(id, { label });
    },
    [updateConnection],
  );

  return {
    connections,
    activeConnection,
    addConnection,
    removeConnection,
    setActive,
    updateLabel,
  };
}
